import axios, { AxiosError, AxiosResponse } from "axios";
import { getApiBaseURL } from "@/lib/utils";

// Types based on API documentation
export interface ApiError {
  detail: string;
  code: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
  status: number;
}

interface ErrorResponse {
  detail?: string;
  code?: string;
  errors?: Array<{
    field: string;
    message: string;
  }>;
  message?: string; // For backwards compatibility
}

// Create axios instance with default config
export const api = axios.create({
  baseURL: getApiBaseURL(),
  timeout: 10000,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json; version=1.0", // API versioning
  },
});

// Request interceptor for API calls
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor for API calls
api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Remove invalid token
      localStorage.removeItem("access_token");

      // Attempt to refresh token if we have a refresh token
      const refreshToken = localStorage.getItem("refresh_token");
      if (refreshToken && originalRequest && !originalRequest._retry) {
        originalRequest._retry = true;
        
        try {
          const response = await axios.post(
            `${getApiBaseURL()}/api/auth/token/refresh/`,
            { refresh: refreshToken },
            {
              headers: {
                "Content-Type": "application/json",
                "Accept": "application/json; version=1.0",
              },
            }
          );

          const { access } = response.data;
          localStorage.setItem("access_token", access);

          // Retry original request with new token
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          // If refresh fails, clear tokens and redirect to login
          localStorage.removeItem("access_token");
          localStorage.removeItem("refresh_token");
          
          // You can dispatch a logout action here or redirect to login
          // window.location.href = "/login";
          return Promise.reject(refreshError);
        }
      }
    }

    // Transform error response according to API documentation format
    const responseData = error.response?.data as ErrorResponse | undefined;
    const apiError: ApiError = {
      detail: responseData?.detail || responseData?.message || "An unexpected error occurred. Please try again.",
      code: responseData?.code || "UNKNOWN_ERROR",
      errors: responseData?.errors,
      status: error.response?.status || 500,
    };

    return Promise.reject(apiError);
  }
);

// Add a declaration to extend AxiosRequestConfig
declare module 'axios' {
  export interface AxiosRequestConfig {
    _retry?: boolean;
  }
}

export default api;
